import { inject, observer } from 'mobx-react';
import React from 'react';
import { Instance } from 'mobx-state-tree';
import { ActionCode, Page, TLCode } from '../../../common/enums';
import {
  AnchorStore,
  GroupStore,
  ApplicationStore,
  AuthenticationStore,
  BankHolidayDateStore,
  BlackPointStore,
  DutyStore,
  PharmacyDetailedStore,
  PharmacyStore,
  SecretariatStore,
  DutyAssignmentStore,
  DutyOffRequestStore,
  SystemStatusStore,
  UserStore,
  UiStore,
} from '../../../store';
import withTranslate, { WithTranslateProps } from '../WithTranslate';
import {
  DutyAssignment,
  BlackPoint,
  BankHolidayDate,
  DutyOffRequest,
  SystemStatus,
  Pharmacy
} from '../../../store/models';
import PharmacyDutiesCalendar from '../../Pharmacy/PharmacyDutiesCalendar';
import moment from 'moment';
import { MultiCalendarSelectionType } from './MultiCalendar';
import { DutyToSendToAnotherCalendar } from '../../../store/interfaces/DutyToSend';
import { PharmacyViews } from '../../../store/PharmacyStore';
import { number } from 'mobx-state-tree/dist/internal';
import { DutyExchangeItemStore } from '../../../store/DutyExchangeItemStore';
import { DutyType } from '../../../store/models/DutyType';

interface SimplePharmacyCalendarProps extends WithTranslateProps {
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  BankHolidayDateStore?: Instance<typeof BankHolidayDateStore>;
  BlackPointStore?: Instance<typeof BlackPointStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  DutyOffRequestStore?: Instance<typeof DutyOffRequestStore>;
  DutyStore?: Instance<typeof DutyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  AnchorStore?: Instance<typeof AnchorStore>;
  GroupStore?: Instance<typeof GroupStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  UserStore?: Instance<typeof UserStore>;
  UiStore?: Instance<typeof UiStore>;
  DutyExchangeItemStore?: Instance<typeof DutyExchangeItemStore>;
  onOtherPharmacyChange?: (otherPharmacyId: Instance<typeof number> |undefined) => void;

  pharmacyId: number;
  hideLegend?: boolean;
  synchronizedScrolling?: boolean;
  fly?: number;
  sly?: number;
  onCalendarMove?: (fly: number, sly: number) => void;
  onTypeChange?: (type: MultiCalendarSelectionType | undefined) => void;
  selectedType?: MultiCalendarSelectionType | undefined;
  separateToolbox?: boolean;

  inMultiCalendar?: boolean;
  onDutyToSendToAnotherPharmacy?: (date: Date, dutyType: 'NIGHT' | 'DAY' | 'DAYNIGHT', pharmacyId: number) => void;
  otherPharmacyBusyDates?: Array<Date>;
  setPharmacyBusyDates?: (dates: Date[]) => void;
  dutySentToAnotherPharmacy?: DutyToSendToAnotherCalendar;
  onDutySentConsumed?: () => void;

  onPharmacyIdChanged?: (pharmacyId: number | undefined) => void;

  groupIdToLimitTo?: number;
}

interface SimplePharmacyCalendarState {
  bankHolidayDates: Array<Instance<typeof BankHolidayDate>>;
  blackPointList: Array<Instance<typeof BlackPoint>>;
  dutyAssignments: Array<Instance<typeof DutyAssignment>>;
  dutyOffRequests: Array<Instance<typeof DutyOffRequest>>;
  systemStatusForSecretariat: Array<Instance<typeof SystemStatus>>;
  isLoading: boolean;
  pharmacyId: number | undefined;
  fly: number;
  sly: number;
  systemStatus?: any;
  selectedType?: MultiCalendarSelectionType;
  exchangeItemHistory: any[];
}

@inject('ApplicationStore')
@inject('AuthenticationStore')
@inject('BankHolidayDateStore')
@inject('BlackPointStore')
@inject('DutyAssignmentStore')
@inject('DutyOffRequestStore')
@inject('DutyStore')
@inject('PharmacyDetailedStore')
@inject('PharmacyStore')
@inject('AnchorStore')
@inject('GroupStore')
@inject('SystemStatusStore')
@inject('SecretariatStore')
@inject('UserStore')
@inject('UiStore')
@inject('DutyExchangeItemStore')
@observer
class SimplePharmacyCalendar extends React.Component<
  SimplePharmacyCalendarProps & WithTranslateProps,
  SimplePharmacyCalendarState
> {
  state = {
    bankHolidayDates: [],
    blackPointList: [],
    dutyAssignments: [],
    dutyOffRequests: [],
    systemStatusForSecretariat: [],
    isLoading: true,
    fly: Number(this.props.fly),
    sly: Number(this.props.sly),
    pharmacyId: this.props.pharmacyId === undefined ? undefined : Number(this.props.pharmacyId),
    systemStatus: null,
    selectedType: this.props.selectedType ? this.props.selectedType : undefined,
    exchangeItemHistory: []
  };

  _isMounted = false;

  onTypeChange = (type: MultiCalendarSelectionType) => {
    if (this.state.selectedType !== type) {
      this.setState({ selectedType: type });
      if (this.props.onTypeChange) this.props.onTypeChange(type);
    }
  };

  async fetchData() {
    if (this.state.pharmacyId !== undefined) this.updateData(this.state.pharmacyId);
  }

  async updateData(newPharmacyId: number) {
    var pharmacyId: number = newPharmacyId;
    const { AuthenticationStore, ApplicationStore, PharmacyStore, PharmacyDetailedStore } = this.props;

    await PharmacyStore!.loadListSafely(true, true);

    this._isMounted && this.setState({ isLoading: true, pharmacyId });

    let isPharmacy = false;
    if (
      AuthenticationStore!.userActionCodes.includes(ActionCode.PHARMACY_USER_CODE) &&
      pharmacyId !== AuthenticationStore!.pharmacyId
    ) {
      ApplicationStore!.navigate(Page.Unauthorized);
    } else if (AuthenticationStore!.userActionCodes.includes(ActionCode.PHARMACY_USER_CODE)) {
      isPharmacy = true;
    }

    const pharmacy = PharmacyStore!.getById(pharmacyId)!;
    this._isMounted && PharmacyDetailedStore!.getByIdWithDetails(pharmacyId);
    await Promise.all([
      this.searchDuties({ pharmacyId, view: PharmacyViews.DETAILED }),
      this.searchBlackPoints({ pharmacyId }),
      this.loadDataForCalendar(pharmacyId, pharmacy ? pharmacy.secretariatId : -1),
      this.props.UserStore!.loadList,
    ]).then((responses) => {
      if (!this._isMounted) return;

      const nextAcceptableBlackpoint = this.state.systemStatus
        ? moment(this.state.systemStatus![0]['status'], 'DD/MM/YYYY').format('YYYY-MM-DD')
        : '';

      //failsafe
      const blackpointNoInMobxStateTreeAnymore = responses[1].length != 0 && responses[1][0].type == undefined;
      if (!blackpointNoInMobxStateTreeAnymore) {
        this._isMounted &&
          this.setState({
            dutyAssignments: isPharmacy
              ? responses[0].filter((da) => da.duty.dutyDate.date <= nextAcceptableBlackpoint)
              : responses[0],
            blackPointList: responses[1],
            ...responses[2],
            isLoading: false,
            pharmacyId: pharmacyId,
            fly: Number(this.props.fly),
            sly: Number(this.props.sly),
          });
        if (this.props.setPharmacyBusyDates !== undefined) {
          const bpDates = (responses[1] as Array<Instance<typeof BlackPoint>>).map((bp) => moment(bp.date).toDate());
          const dutiesDates = (responses[0] as Array<Instance<typeof DutyAssignment>>).map((duty) =>
            moment(duty.duty!.dutyDate.date).toDate(),
          );
          this._isMounted && this.props.setPharmacyBusyDates([...bpDates, ...dutiesDates]);
        }
      }
    });
  }

  componentDidUpdate(prevProps: SimplePharmacyCalendarProps) {
    if (prevProps.pharmacyId !== this.props.pharmacyId){
        this.searchHistory(this.props.pharmacyId, false, new Date().getFullYear() - 1)
    }

    if (prevProps.pharmacyId !== this.props.pharmacyId) {
      this.setState({ pharmacyId: this.props.pharmacyId }, () => {
        this.fetchData();
      });
    }
    if (this.props.selectedType !== this.state.selectedType)
      this._isMounted &&
        this.setState({
          selectedType: this.props.selectedType,
        });
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.props.AnchorStore!.loadListSafely(true, true);
    await this.props.GroupStore!.loadListSafely(true, true);
    const criteria: any = {
      subSystem: 'lastPublishedDuty',
    };
    const systemStatus = await this.props.SystemStatusStore!.search(criteria);
    this._isMounted && this.setState({ systemStatus });
    await this.fetchData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  searchBlackPoints = async (criteria: any) => {
    let result;
    await Promise.all([this.props.BlackPointStore!.search(criteria)])
      .catch((e) => console.log(e))
      .then((responses) => (result = responses[0]));

    return result;
  };

  searchDuties = async (criteria: any) => {
    const { DutyAssignmentStore, PharmacyStore, DutyStore } = this.props;
    let result;
    await Promise.all([
      DutyAssignmentStore!.searchAssignment(criteria),
      PharmacyStore!.searchForDutyAssignment(criteria),
      DutyStore!.searchForDutyAssignment(criteria),
    ])
      .catch(console.error)
      .then((responses) => (result = responses[0]));

    return result;
  };

  searchDutyOffRequests = async (criteria: any) => {
    let result;
    await Promise.all([this.props.DutyOffRequestStore!.searchDutyOffRequest(criteria)])
      .catch((e) => console.log(e))
      .then((responses) => (result = responses[0]));

    return result;
  };

  loadDataForCalendar = async (pharmacyId: number, secretariatId: number | undefined) => {
    let result = {};
    const promises = [
      this.props.BankHolidayDateStore!.loadListSafely(true, true),
      this.props.DutyOffRequestStore!.searchDutyOffRequest({ pharmacyId }),
    ] as Array<Promise<any>>;
    if (secretariatId != null) {
      const criteriaBP: any = { secretariatId };
      promises.push(this.props.SystemStatusStore!.search(criteriaBP));
    }

    await Promise.all(promises)
      .catch((e) => console.log(e))
      .then((responses) => {
        if (!this._isMounted) return;

        if (responses) {
          result = Object.assign(result, { bankHolidayDates: responses[0], dutyOffRequests: responses[1] });
          if (responses.length === 3) result = Object.assign(result, { systemStatusForSecretariat: responses[2] });
        }
      });

    return result;
  };

  searchHistory = async(
    pharmacyId: number,
    currentValueOnlyUndownloadedValues: boolean | undefined,
    year: number | undefined = undefined,
  ) => {

    var isPharmacy = this.props.AuthenticationStore!.userActionCodes.includes(ActionCode.PHARMACY_USER_CODE);

    // const pharmacyId = isPharmacy ? this.props.AuthenticationStore!.pharmacyId : this.state.pharmacyId
    if (!pharmacyId) {
      return;
    }

    const criteria = {
      pharmacyId: pharmacyId,
      onlyFutureDutyExchange: currentValueOnlyUndownloadedValues,
      fromYear: currentValueOnlyUndownloadedValues ? undefined : year,
    };

    const responses = await Promise.all([
      this.props.DutyExchangeItemStore!.search(criteria as any),
      this.props.DutyStore!.searchForDutyExchangeItem(criteria as any),
    ]);
    //@ts-ignore
    var exchangeItemHistory = this.buildGridData(responses[0]);
    this._isMounted && this.setState({ exchangeItemHistory });

  }

  buildGridData = (dutyExchangeList: any[]) => {
       return dutyExchangeList.map((elem) => ({
            dutyExchangeItemId: elem.dutyExchangeItemId,
            dutyAssignmentId: elem.dutyAssignmentId,
            recipientId: elem.recipientId,
            date: elem.createdOn,
            sender: elem.sender.apbNumber + ' - ' + elem.sender.name,
            recipient: elem.recipient.apbNumber + ' - ' + elem.recipient.name,
            dutyDate: elem.dutyAssignment.duty ? elem.dutyAssignment.duty.dutyDate.date : '',
            dutyType: elem.dutyAssignment.duty
                ? this.props.translate(this.mapDutyTypeToTLCode(elem.dutyAssignment.duty.dutyType!))
                : '',
            requestedBy: elem.requestorName
        }));
    };

    mapDutyTypeToTLCode = (dutyType: Instance<typeof DutyType>) => {
        switch (dutyType.type) {
            case 'DAY':
                return TLCode.EXCHANGE_HISTORY_Day;
            case 'NIGHT':
                return TLCode.EXCHANGE_HISTORY_Night;
            default:
                return TLCode.dummy;
        }
    };

  saveCalendarChange = async (data: any) => {
    if (this.state.pharmacyId !== undefined) {
      await this.props.PharmacyDetailedStore!.saveCalendarChange(data);
      let bpResp;
      let dutiesResp;
      await this.searchBlackPoints({ pharmacyId: this.state.pharmacyId }).then((resp) => (bpResp = resp));
      await this.searchDuties({ pharmacyId: this.state.pharmacyId, view: PharmacyViews.DETAILED }).then(
        (resp) => (dutiesResp = resp),
      );
      await this.searchDutyOffRequests({ pharmacyId: this.state.pharmacyId }).then(
        (resp) => this._isMounted && this.setState({ dutyOffRequests: resp }),
      );

      this._isMounted && this.setState({ blackPointList: bpResp, dutyAssignments: dutiesResp });
      if (this.props.setPharmacyBusyDates !== undefined) {
        const bpDates = (bpResp as Array<Instance<typeof BlackPoint>>).map((bp) => moment(bp.date).toDate());
        const dutiesDates = (dutiesResp as Array<Instance<typeof DutyAssignment>>).map((duty) =>
          moment(duty.duty!.dutyDate.date).toDate(),
        );
        this._isMounted && this.props.setPharmacyBusyDates([...bpDates, ...dutiesDates]);
      }
    }
  };

  onPharmacyChange = (pharmacy: Instance<typeof Pharmacy>) => {
    if (this.props.onPharmacyIdChanged) this.props.onPharmacyIdChanged(pharmacy.pharmacyId);
    this.updateData(pharmacy.pharmacyId);
    
    this.searchHistory(pharmacy.pharmacyId, false, new Date().getFullYear() - 1)
  };

  onCalendarMove = (fly: number, sly: number) => {
    if (this.props.onCalendarMove) this.props.onCalendarMove(fly, sly);
  };

  showPharmacy = (pharmacy: any) => {
    return (
      pharmacy !== undefined &&
      pharmacy.level !== 0 &&
      ((this.props.AuthenticationStore!.isSuperAdmin && this.props.SecretariatStore!.selectedSecretariatId === 0) ||
        (this.props.AuthenticationStore!.isSuperAdmin &&
          pharmacy !== undefined &&
          this.props.SecretariatStore!.selectedSecretariatId !== undefined &&
          this.props.SecretariatStore!.selectedSecretariatId === pharmacy!.secretariatId) ||
        (pharmacy !== undefined &&
          this.props.AuthenticationStore!.isSecretariat &&
          this.props.AuthenticationStore!.secretariatId === pharmacy!.secretariatId))
    );
  };

  render() {
    const { PharmacyStore, hideLegend, synchronizedScrolling, PharmacyDetailedStore } = this.props;
    const { systemStatusForSecretariat, pharmacyId, blackPointList } = this.state;

    const pharmacyList = PharmacyStore!.dataList;
    const pharmacyDetails = pharmacyId === undefined ? undefined : PharmacyDetailedStore!.getByIdOrNew(pharmacyId);
    const blackpointNoInMobxStateTreeAnymore =
      blackPointList && blackPointList.length !== 0 && blackPointList[0]['type'] === undefined;

    var result;
    if (pharmacyId === undefined || (pharmacyId > 0 && pharmacyDetails && !blackpointNoInMobxStateTreeAnymore)) {
      result = (
        <>
          <PharmacyDutiesCalendar
            rowCount={1}
            hideLegend={hideLegend}
            synchronizedScrolling={synchronizedScrolling}
            bankHolidayDateList={this.state.bankHolidayDates}
            blackPointList={this.state.blackPointList}
            dutyAssignments={this.state.dutyAssignments}
            dutyOffRequests={this.state.dutyOffRequests}
            pharmacyDetails={pharmacyDetails}
            saveCalendarChange={this.saveCalendarChange}
            pharmacyId={this.state.pharmacyId}
            fly={this.props.fly}
            sly={this.props.sly}
            onCalendarMove={this.onCalendarMove}
            systemStatus={systemStatusForSecretariat}
            isSecretariat={
              this.props.AuthenticationStore!.isSecretariat ||
              this.props.AuthenticationStore!.isAdmin ||
              this.props.AuthenticationStore!.isSuperAdmin
            }
            onTypeChange={this.onTypeChange}
            selectedType={this.props.selectedType}
            separateToolbox={this.props.separateToolbox}
            pharmacyList={pharmacyList.filter(this.showPharmacy)}
            onPharmacyChange={this.onPharmacyChange}
            onOtherPharmacyChange={this.props.onOtherPharmacyChange}
            inMultiCalendar={this.props.inMultiCalendar}
            onDutyToSendToAnotherPharmacy={this.props.onDutyToSendToAnotherPharmacy}
            dutySentToAnotherPharmacy={this.props.dutySentToAnotherPharmacy}
            onDutySentConsumed={this.props.onDutySentConsumed}
            otherPharmacyBusyDates={this.props.otherPharmacyBusyDates}
            userList={this.props.UserStore!.dataList}
            groupIdToLimitTo={this.props.groupIdToLimitTo}
            exchangeHistoryList={this.state.exchangeItemHistory}
          />
        </>

        
      );
    } else {
      result = (
        <div style={{ height: '45vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <h4>Please wait...</h4>
        </div>
      );
    }

    return result;
  }
}

export default withTranslate(SimplePharmacyCalendar);
